import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Bookmark, ChevronDown, Plus, Pencil, Trash2, Share2, Star } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { useWorkspaceHeader, type EventsWorkspaceState } from '../../context/WorkspaceHeaderContext'
import { workspacesApi } from '../../api/workspaces'
import { WorkspaceModal } from './WorkspaceModal'
import { ConfirmModal } from '../ConfirmModal'
import type { Workspace } from '../../types'

export function WorkspacePicker() {
  const { t } = useTranslation()
  const { user, canWrite } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { getState, getOnRestore } = useWorkspaceHeader()
  const [isOpen, setIsOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingWorkspace, setEditingWorkspace] = useState<Workspace | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Workspace | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isAdmin = user?.role === 'Admin'

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const { data: workspaces = [] } = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => workspacesApi.getWorkspaces(),
  })

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof workspacesApi.createWorkspace>[0]) => workspacesApi.createWorkspace(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      toast.success(t('workspaces.created'))
      setModalOpen(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Workspace> }) => workspacesApi.updateWorkspace(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      toast.success(t('workspaces.updated'))
      setModalOpen(false)
      setEditingWorkspace(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => workspacesApi.deleteWorkspace(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      toast.success(t('workspaces.deleted'))
      setConfirmDelete(null)
    },
  })

  const buildStatePayload = (state: EventsWorkspaceState) => ({
    filterExpression: state.filter,
    timeRange: state.timeRange,
    startTime: state.startTime,
    endTime: state.endTime,
    search: state.search,
    application: state.application,
    levels: state.levels,
    viewMode: state.viewMode,
    hasExceptionFilter: state.hasExceptionFilter,
    hasAnnotationsFilter: state.hasAnnotationsFilter,
    annotationSeverities: state.selectedAnnotationSeverities,
  })

  const handleSave = (data: { name: string; description?: string; isShared: boolean; isDefault: boolean }) => {
    const state = getState()
    if (!state) return

    if (editingWorkspace) {
      updateMutation.mutate({ id: editingWorkspace.id, data: { ...data, ...buildStatePayload(state) } })
    } else {
      createMutation.mutate({
        name: data.name,
        description: data.description,
        isShared: data.isShared,
        isDefault: data.isDefault,
        ...buildStatePayload(state),
        signalIds: [],
      })
    }
  }

  const handleRestore = (workspace: Workspace) => {
    getOnRestore()?.(workspace)
    setIsOpen(false)
  }

  const handleEdit = (e: React.MouseEvent, workspace: Workspace) => {
    e.stopPropagation()
    setEditingWorkspace(workspace)
    setModalOpen(true)
    setIsOpen(false)
  }

  const handleDelete = (e: React.MouseEvent, workspace: Workspace) => {
    e.stopPropagation()
    setConfirmDelete(workspace)
    setIsOpen(false)
  }

  const canEditWorkspace = (ws: Workspace) => isAdmin || ws.ownerId === user?.id

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 lg:px-4 py-1.5 text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 rounded-lg hover:bg-white dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600 transition-colors w-full lg:w-[36rem]"
        aria-label={t('workspaces.title')}
        aria-expanded={isOpen}
      >
        <Bookmark size={14} className="shrink-0" />
        <span className="truncate">{t('workspaces.title')}</span>
        <ChevronDown size={14} className={`ml-auto shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[calc(100vw-2rem)] sm:w-[36rem] bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-xl z-50">
          <div className="p-2 border-b dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 px-2">
              {t('workspaces.title')}
            </span>
            {canWrite && (
              <button
                onClick={() => { setEditingWorkspace(null); setModalOpen(true); setIsOpen(false) }}
                className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                title={t('workspaces.saveCurrentState')}
              >
                <Plus size={14} />
                {t('workspaces.save')}
              </button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto">
            {workspaces.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                {t('workspaces.noWorkspaces')}
              </div>
            ) : (
              workspaces.map(ws => (
                <button
                  key={ws.id}
                  onClick={() => handleRestore(ws)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {ws.isDefault && <Star size={12} className="text-amber-500 flex-shrink-0" />}
                      {ws.isShared && <Share2 size={12} className="text-blue-500 flex-shrink-0" />}
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {ws.name}
                      </span>
                    </div>
                    {ws.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {ws.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-0.5">
                      {ws.timeRange && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                          {ws.timeRange}
                        </span>
                      )}
                      {ws.application && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded truncate max-w-[100px]">
                          {ws.application}
                        </span>
                      )}
                      {ws.levels.length > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded">
                          {ws.levels.join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  {canWrite && canEditWorkspace(ws) && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <button
                        onClick={(e) => handleEdit(e, ws)}
                        className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded"
                        title={t('workspaces.edit')}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={(e) => handleDelete(e, ws)}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded"
                        title={t('workspaces.deleteAction')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      <WorkspaceModal
        isOpen={modalOpen}
        workspace={editingWorkspace}
        onClose={() => { setModalOpen(false); setEditingWorkspace(null) }}
        onSave={handleSave}
        isAdmin={isAdmin}
      />

      <ConfirmModal
        isOpen={!!confirmDelete}
        title={t('workspaces.deleteConfirmTitle')}
        message={t('workspaces.deleteConfirm', { name: confirmDelete?.name })}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  )
}
